'use client';

import type { ReactNode } from 'react';
import { memo } from 'react';

import type { SerializedBlock } from '@zoomit/editor';
import { Col, Flex, Spacing } from '@zoomit/primitives';
import {
  AdvertisingParser,
  BlockContainer,
  getBlockContainerFloat,
  getBlockContainerType,
  isMobile,
  renderRemoteBlock,
  useAdvertisingStore,
} from '@zoomit/web-kit';

type Props = {
  articleData: SerializedBlock[];
  hasSidebar: boolean;
  advertisingPositionIndexes?: number[]; // in desktop we don't have advertising between article contents.
};

const ArticleParser = (props: Props) => {
  const { articleData, hasSidebar, advertisingPositionIndexes = [] } = props;

  const mappedAds = useAdvertisingStore((state) => state.mappedModules);
  const dynamicAdModule = mappedAds['mainContent_mobile']?.[0];

  const renderFloatBlocks = (
    firstBlock: SerializedBlock,
    secondBlock: SerializedBlock,
    float: 'right' | 'left',
  ) => (
    <Flex
      flexDirection={float === 'right' ? 'row' : 'row-reverse'}
      gutter={isMobile ? 0 : Spacing.x6}
      alignItems="center"
    >
      <Col size={12} md={6}>
        {renderRemoteBlock(firstBlock, hasSidebar)}
      </Col>
      <Col size={12} md={6}>
        {renderRemoteBlock(secondBlock, hasSidebar)}
      </Col>
    </Flex>
  );

  const renderBlocks = (blocks: SerializedBlock[]) => {
    //TODO: below codes become a bit dirty, we need to refactor it.
    const elements: ReactNode[] = [];
    let displayIndex = 0;
    for (let currIndex = 0; currIndex < blocks.length; ) {
      const currBlock = blocks[currIndex];

      if (isMobile) {
        //----------advertising----------------------------------------------------------------------
        if (currIndex === advertisingPositionIndexes[displayIndex]) {
          elements.push(
            <AdvertisingParser
              module={dynamicAdModule}
              key={`ad-${displayIndex}`}
              startDisplayIndex={displayIndex}
              closeButtonKind="general"
              spacing={{ mb: 'x4', mx: 'auto', px: 'x4' }}
            />,
          );
          displayIndex++;
        }
        //----------block----------------------------------------------------------------------------
        //in mobile, all blocks are vertical.
        elements.push(
          <BlockContainer
            hasSidebar={hasSidebar}
            key={`${currBlock.id}-${currIndex}`}
            template={(currBlock as any).template}
          >
            {renderRemoteBlock(currBlock, hasSidebar)}
          </BlockContainer>,
        );
        currIndex++;
      } else {
        // we don't have dynamic advertising in desktop platform
        // some block templates (e.g. related-article, keyword, ...) change next block template.
        //----------block----------------------------------------------------------------------------
        const currBlockTemplate = (currBlock as any).template;
        const currBlockTemplateType = getBlockContainerType(currBlockTemplate);
        const currBlockFloat = getBlockContainerFloat(currBlockTemplate);
        const sameBlocksContainer = [];
        let numOfBlocksInSameContainer = 0;

        if (
          currBlockFloat !== 'normal' &&
          currIndex !== blocks.length - 1 &&
          blocks[currIndex + 1].type === 'typography'
        ) {
          sameBlocksContainer.push(renderFloatBlocks(currBlock, blocks[currIndex + 1], currBlockFloat));
          numOfBlocksInSameContainer += 2;
        } else {
          sameBlocksContainer.push(renderRemoteBlock(currBlock, hasSidebar));
          numOfBlocksInSameContainer++;
        }

        for (let nextIndex = currIndex + numOfBlocksInSameContainer; nextIndex < blocks.length; nextIndex++) {
          const nextBlock = blocks[nextIndex];
          const nextBlockTemplate = (nextBlock as any).template;
          const nextBlockTemplateType = getBlockContainerType(nextBlockTemplate);
          const nextBlockFloat = getBlockContainerFloat(nextBlockTemplate);

          if (currBlockTemplateType === nextBlockTemplateType) {
            if (
              nextBlockFloat !== 'normal' &&
              nextIndex !== blocks.length - 1 &&
              blocks[nextIndex + 1].type === 'typography'
            ) {
              sameBlocksContainer.push(renderFloatBlocks(nextBlock, blocks[nextIndex + 1], nextBlockFloat));
              numOfBlocksInSameContainer += 2;
              nextIndex++;
            } else {
              sameBlocksContainer.push(renderRemoteBlock(nextBlock, hasSidebar));
              numOfBlocksInSameContainer++;
            }
          } else break;
        }

        elements.push(
          <BlockContainer
            hasSidebar={hasSidebar}
            key={`${currBlock.id}-${currIndex}`}
            template={currBlockTemplate}
          >
            {sameBlocksContainer}
          </BlockContainer>,
        );
        currIndex += numOfBlocksInSameContainer;
      }
    }

    return elements;
  };

  return (
    <Flex flexDirection="column" gap={1.6} flexWrap="nowrap">
      {renderBlocks(articleData)}
    </Flex>
  );
};

export default memo(ArticleParser);
